import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFolder } from "src/core/contexts/FolderContext";
import {
  EditContactFieldsValues,
  editContactSchema,
} from "src/core/forms/contact/editContact.schema";
import {
  useFollowFolderMutation,
  useUnfollowFolderMutation,
  useUpdateContactsMutation,
} from "src/core/gql/graphql";
import { useToast } from "src/core/hooks/use-toast";
import { Contact, Owner } from "src/core/types/contact";
import { buildUpdateContactVariables } from "src/core/utils/contact";

/**
 * Hook pour le formulaire d'édition d'un contact.
 */
export function useContactFormController(
  customerFolderId: string,
  setOpen: (open: boolean) => void,
  contactPerson: Contact | null,
  owner: Owner | null,
  onSuccess: () => void
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { folderFile } = useFolder();
  const [follow] = useFollowFolderMutation();
  const [unfollow] = useUnfollowFolderMutation();

  const form = useForm<EditContactFieldsValues>({
    resolver: zodResolver(editContactSchema()),
    defaultValues: {
      prenom: contactPerson?.firstName || "",
      nom: contactPerson?.lastName || "",
      email: contactPerson?.email || "",
      telephone: contactPerson?.phone || "",
      expertComptable: owner?.id || "",
      isFollowing: folderFile.folderFile?.currentCustomerFolder?.followedByCurrentUser || false,
      folowers:
        folderFile.folderFile?.currentCustomerFolder?.followingUsers?.map((user) => user.id) || [],
    },
  });
  const [updateContacts] = useUpdateContactsMutation();

  const onSubmit = useCallback(
    async (formData: EditContactFieldsValues) => {
      try {
        setIsLoading(true);
        const variables = buildUpdateContactVariables(
          customerFolderId,
          formData,
          contactPerson,
          owner
        );

        await updateContacts({
          variables,
        });

        if (
          formData.isFollowing &&
          !folderFile.folderFile?.currentCustomerFolder?.followedByCurrentUser
        ) {
          await follow({ variables: { customerFolderId } });
        }
        if (
          !formData.isFollowing &&
          folderFile.folderFile?.currentCustomerFolder?.followedByCurrentUser
        ) {
          await unfollow({ variables: { customerFolderId } });
        }

        const currentFollowers =
          folderFile.folderFile?.currentCustomerFolder?.followingUsers?.map((user) => user.id) ||
          [];
        const newFollowers = formData.folowers || [];

        const followersToRemove = currentFollowers.filter(
          (userId) => !newFollowers.includes(userId)
        );

        const followersToAdd = newFollowers.filter((userId) => !currentFollowers.includes(userId));

        if (followersToRemove.length > 0) {
          await Promise.all(
            followersToRemove.map(async (userId) => {
              await unfollow({ variables: { customerFolderId, userId } });
            })
          );
        }

        if (followersToAdd.length > 0) {
          await Promise.all(
            followersToAdd.map(async (userId) => {
              await follow({ variables: { customerFolderId, userId } });
            })
          );
        }
        toast({
          description: t("form.editContact.success"),
        });
        onSuccess?.();
      } catch (error) {
        console.error("useEditContactForm::onSubmit", error);
        toast({
          description: t("form.editContact.error"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        setOpen(false);
      }
    },
    [
      contactPerson,
      owner,
      customerFolderId,
      t,
      toast,
      updateContacts,
      onSuccess,
      setOpen,
      follow,
      unfollow,
      folderFile,
    ]
  );

  // Réinitialiser le formulaire seulement quand l'ID du contact change (changement de dossier)
  useEffect(() => {
    form.reset({
      prenom: contactPerson?.firstName || "",
      nom: contactPerson?.lastName || "",
      email: contactPerson?.email || "",
      telephone: contactPerson?.phone || "",
      expertComptable: owner?.id || "",
      isFollowing: folderFile.folderFile?.currentCustomerFolder?.followedByCurrentUser || false,
      folowers:
        folderFile.folderFile?.currentCustomerFolder?.followingUsers?.map((user) => user.id) || [],
    });
  }, [contactPerson?.id, owner?.id, form, contactPerson, owner, folderFile]);

  return {
    form,
    isLoading,
    onSubmit,
  };
}
